/**
 * NeedWarning — the ONE card-level "you haven't set this up yet" state
 * (founder 2026-07-13). When a card needs something the account hasn't provided,
 * it renders THIS in place of the number: the founder's amber warning (never
 * red — nothing is broken and nobody is losing money) with the need's
 * plain-language line and a one-click link into the exact place that fixes it.
 *
 * It REPLACES the old "sample number behind a SAMPLE badge" locked state on
 * gated cards — the founder killed pretend fill-ins on the profit model, and
 * this extends that ruling to every gated card. Demo/test accounts have full
 * seeded data, so their cards never reach this state.
 *
 * This is the same universal pattern as CmGapNotice (the 2026-07-11 costs-gap
 * warning) — and for the product_costs need it DELEGATES to CmGapNotice so the
 * "N products missing costs (names…) → Fix it" copy and its pre-scoped costs
 * chat stay identical. Every other need renders the shared amber block below.
 */

import { Link } from "react-router-dom";
import { NEEDS, type NeedId, type NeedCmGap } from "@data-dashboard/shared";
import { CmGapNotice, CmGapCompact } from "./CmGapNotice.js";

const AMBER = "#d97706";

/**
 * The card warning for one unmet need. `cmGap` is only meaningful for
 * product_costs (the partial-costing detail); when present we hand off to
 * CmGapNotice so the exact-products copy + costs-chat scoping are reused.
 * `compact` uses the tighter layout for small lever/hero tiles.
 */
export function NeedWarning({
  need,
  cmGap,
  compact = false,
}: {
  need: NeedId;
  cmGap?: NeedCmGap | null;
  compact?: boolean;
}): React.ReactElement {
  // product_costs with a partial gap → reuse the existing costs-gap warning
  // verbatim (names the products + opens the costs chat pre-scoped to them).
  if (cmGap && cmGap.missingProducts > 0) {
    return compact ? <CmGapCompact gap={cmGap} /> : <CmGapNotice gap={cmGap} />;
  }

  const def = NEEDS[need];
  const link = (
    <Link
      to={def.fixTo}
      onClick={(e) => e.stopPropagation()}
      style={{ color: AMBER, fontWeight: 700, fontSize: compact ? 10.5 : 12.5, textDecoration: "none", whiteSpace: "nowrap" }}
    >
      {def.fixCta} →
    </Link>
  );

  if (compact) {
    return (
      <div>
        <div style={{ color: AMBER, fontSize: 14.5, fontWeight: 800, margin: "5px 0 2px" }}>
          <span aria-hidden="true">⚠️</span> Can't calculate yet
        </div>
        <div style={{ fontSize: 10.5, color: "var(--body)", lineHeight: 1.45 }}>
          {def.cardWarning} {link}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      style={{
        borderRadius: 10,
        border: `1px solid color-mix(in srgb, ${AMBER} 45%, var(--border))`,
        background: `color-mix(in srgb, ${AMBER} 8%, transparent)`,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: AMBER, fontWeight: 800, fontSize: 13 }}>
        <span aria-hidden="true">⚠️</span> Not set up yet
      </div>
      <div style={{ marginTop: 5, fontSize: 12.5, color: "var(--body)", lineHeight: 1.55 }}>
        {def.cardWarning} {link}
      </div>
    </div>
  );
}
